import {Component, ElementRef, HostBinding, Inject, Input, OnChanges, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {inputFeedbackTransition} from '../animations';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  Credential,
  PasswordCredential,
  PasswordCredentialInfo,
  PasswordService,
  User,
  WebAuthnCredentialInfo,
  WebAuthnPublicKeyCredential,
  WebAuthnService
} from '../../gen';
import {startAuthentication} from '@simplewebauthn/browser';
import {AppConstants} from '../AppConstants';
import {AppStateService} from '../app-state.service';
import {StandaloneComponent} from '../standalone.component';

@Component({
  selector: 'app-authorize-action',
  templateUrl: './authorize-action.component.html',
  styleUrls: ['./authorize-action.component.scss'],
  animations: [
    inputFeedbackTransition,
  ],
  // Looks like all styles are ignored without this random setting, unless the component is "page level". Don't ask what that means,
  // its probably so obvious that there is no need to document it.
  encapsulation: ViewEncapsulation.None,
})
export class AuthorizeActionComponent implements OnInit, OnChanges {

  private configuration: ConfigurationService;

  @Input() submitHandler: (creds: Credential) => void;
  @Input() closeHandler: () => void;
  @Input() profile: User;
  @Input() class: string;
  @Input() error: { error: string, error_description: string }|undefined;
  @ViewChild('tabHolder') tabHolder: ElementRef;
  @ViewChild('passwordTab') passwordTab: ElementRef;
  @ViewChild('passkeyTab') passkeyTab: ElementRef;
  formGroup: FormGroup;
  tabs: String[] = ['passkey', 'password'];
  activeTab: string|undefined;
  passkeyActive: boolean|string;
  passkeyCred: Credential|undefined;
  hasPasskey: boolean|undefined;
  hasPassword: boolean|undefined;

  // TODO: THis may not be needed or used at all, investigate and refactor if so
  @HostBinding('@inputFeedbackTransition') trigger = '';
  @HostBinding('@.disabled') disableAnimations(): boolean {
    const enabled = this.configuration.getProperties().enableAnimations;
    // for disabling our java client lib anims append: |javafx\/
    const not_supported: boolean = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    return !enabled || not_supported;
  }

  constructor(
    configuration: ConfigurationService,
    @Inject('PasswordApi') protected passwordApi: PasswordService,
    @Inject('WebAuthnApi') protected webAuthnApi: WebAuthnService,
    private appStateService: AppStateService,
  ) {
    this.configuration = configuration;
    this.onPasswordLoaded = this.onPasswordLoaded.bind(this);
    this.onPasskeyLoaded = this.onPasskeyLoaded.bind(this);
  }
  ngOnInit() {
    this.init();
    this.formGroup = new FormGroup({
      password: new FormControl('', [Validators.required]),
    });
    this.webAuthnApi.listWebAuthnCredentials().subscribe(this.onPasskeyLoaded, (err) => {
      this.onPasskeyLoaded(undefined);
    });
    this.passwordApi.listPasswordCredentialInfo().subscribe(this.onPasswordLoaded, (err) => {
      this.onPasswordLoaded(undefined);
    });
  }

  protected onPasswordLoaded(body: PasswordCredentialInfo[]|undefined) {
    // We only support 2 an one mu
    if (!!body && body.length) {
      this.hasPassword = true;
    } else {
      this.hasPassword = false;
    }
    if (this.hasPasskey !== undefined) {
      // both loaded
      this.selectTab();
    }
  }
  protected onPasskeyLoaded(body: WebAuthnCredentialInfo[]|undefined) {
    if (!!body && body.length) {
      this.hasPasskey = true;
    } else {
      this.hasPasskey = false;
    }
    if (this.hasPassword !== undefined) {
      // both loaded
      this.selectTab();
    }
  }

  /**
   * Selects passkey if it was used for sign in, otherwise password
   * @private
   */
  private selectTab() {
    const t = this.appStateService.getAppState().getAuthenticationState().getExistingAuthentications();
    if (t &&
      t.length &&
      !!t.find((f) => f.authenticationMethod === AppConstants.AC_AM_WEB_AUTHN_PUBLIC_KEY)) {
      this.activeTab = 'passkey';
      if (!!this.passkeyTab) {
        // not really sire how this could be missing, but whatever.
        StandaloneComponent.setFocusToElemOrChildControl(this.passkeyTab);
      }
    } else {
      this.activeTab = 'password';
      if (!!this.passwordTab) {
        // not really sire how this could be missing, but whatever.
        StandaloneComponent.setFocusToElemOrChildControl(this.passwordTab);
      }
    }
  }

  ngOnChanges() {
    this.init();
  }

  init() {

  }

  triggerPasskey(auto?: boolean) {
    this.passkeyActive = true;
    this.webAuthnApi.startAuthentication({ name: this.profile.email }).subscribe((r) => {
      this.passkeyActive = r.requestId;
      const options = JSON.parse(r.publicKeyCredentialRequestOptions);
      startAuthentication(options, auto).then((f) => {
        this.passkeyCred = {
          authenticationMethod: 'webAuthnPublicKey',
          requestId: r.requestId,
          publicKeyCredential: JSON.stringify(f),
        } as WebAuthnPublicKeyCredential;
        this.passkeyActive = false;
        this.onSubmit();
      }, (ee) => {
        if (this.passkeyActive === r.requestId) {
          this.passkeyActive = false;
        }
      });
    }, (err) => {
      this.passkeyActive = false;
    });
  }
  changeTab(t: string) {
    if (t && t !== this.activeTab && this.tabs.indexOf(t) >= 0) {
      this.activeTab = t;
    }
  }
  onSubmit() {
    if (this.activeTab === 'password') {
      this.submitHandler({
        authenticationMethod: 'passwordOnly',
        password: this.formGroup.get('password').value
      } as PasswordCredential);
    } else if (this.activeTab === 'passkey') {
      this.submitHandler(this.passkeyCred);
    } else {
      this.submitHandler(undefined);
    }
  }
}
